import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { PRIORITY_LEVELS, type BoardFilters } from '@/lib/tasks-board'
import type { Carteira } from '@/services/task-board-service'
import type { TeamMember } from '@/services/team-service'

interface TaskBoardFiltersProps {
  filters: BoardFilters
  members: TeamMember[]
  carteiras: Carteira[]
  onChange: (patch: Partial<BoardFilters>) => void
}

/** Filtros persistidos na URL: responsável, carteira, prioridade, processo e busca. */
export function TaskBoardFilters({ filters, members, carteiras, onChange }: TaskBoardFiltersProps) {
  const [processo, setProcesso] = useState(filters.processo ?? '')
  const [busca, setBusca] = useState(filters.busca ?? '')
  useEffect(() => setProcesso(filters.processo ?? ''), [filters.processo])
  useEffect(() => setBusca(filters.busca ?? ''), [filters.busca])

  const mine = filters.vista === 'minha-semana'
  const active = [
    !mine && filters.responsavel && { key: 'responsavel', label: filters.responsavel === 'eu' ? 'Responsável: eu' : `Responsável: ${members.find((m) => m.id === filters.responsavel)?.fullName ?? 'colega'}` },
    filters.carteira && { key: 'carteira', label: `Carteira: ${carteiras.find((c) => c.id === filters.carteira)?.nome ?? '—'}` },
    filters.prioridade && { key: 'prioridade', label: filters.prioridade },
    filters.processo && { key: 'processo', label: `Processo: ${filters.processo}` },
    filters.busca && { key: 'busca', label: `"${filters.busca}"` },
    filters.cliente && { key: 'cliente', label: 'Cliente da ficha' },
  ].filter(Boolean) as Array<{ key: keyof BoardFilters; label: string }>

  return (
    <div className="flex flex-col gap-2">
      <form
        className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5"
        onSubmit={(e) => {
          e.preventDefault()
          onChange({ processo: processo.trim() || undefined, busca: busca.trim() || undefined })
        }}
      >
        <NativeSelect
          aria-label="Responsável"
          value={mine ? 'eu' : filters.responsavel ?? ''}
          disabled={mine}
          onChange={(e) => onChange({ responsavel: e.target.value || undefined })}
        >
          <option value="">Responsável: todos</option>
          <option value="eu">Eu</option>
          {members.map((m) => <option key={m.id} value={m.id}>{m.fullName || m.email}</option>)}
        </NativeSelect>
        <NativeSelect aria-label="Carteira" value={filters.carteira ?? ''} onChange={(e) => onChange({ carteira: e.target.value || undefined })}>
          <option value="">Carteira: todas</option>
          {carteiras.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </NativeSelect>
        <NativeSelect
          aria-label="Prioridade"
          value={filters.prioridade ?? ''}
          onChange={(e) => onChange({ prioridade: (e.target.value || undefined) as BoardFilters['prioridade'] })}
        >
          <option value="">Prioridade: todas</option>
          {PRIORITY_LEVELS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </NativeSelect>
        <Input aria-label="Processo" placeholder="Nº do processo" value={processo} onChange={(e) => setProcesso(e.target.value)}
          onBlur={() => onChange({ processo: processo.trim() || undefined })} />
        <Input aria-label="Buscar tarefas" placeholder="Buscar tarefa, cliente…" value={busca} onChange={(e) => setBusca(e.target.value)}
          onBlur={() => onChange({ busca: busca.trim() || undefined })} />
        <button type="submit" className="sr-only">Aplicar filtros</button>
      </form>
      {active.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {active.map((chip) => (
            <Badge key={chip.key} variant="secondary" className="gap-1">
              {chip.label}
              <button type="button" aria-label={`Remover filtro ${chip.label}`} onClick={() => onChange({ [chip.key]: undefined })}>
                <X className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}
